import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  ShoppingBag,
  AccessTime,
  Build,
  CheckCircle,
  Cancel,
  Visibility,
  ArrowBack
} from '@mui/icons-material';
import orderService from '../services/orderService';
import uploadService from '../services/uploadService';
import { useNavigate } from 'react-router-dom';

const STATUS_CONFIG = {
  pendiente: {
    color: 'warning',
    icon: <AccessTime />,
    label: 'Pendiente',
    description: 'Tu pedido está esperando ser procesado'
  },
  procesando: {
    color: 'info',
    icon: <Build />,
    label: 'En Proceso',
    description: 'El vendedor está revisando tu pedido'
  },
  aprobado: {
    color: 'success',
    icon: <CheckCircle />,
    label: 'Aprobado',
    description: 'Tu pedido ha sido aprobado y procesado'
  },
  rechazado: {
    color: 'error',
    icon: <Cancel />,
    label: 'Rechazado',
    description: 'Tu pedido fue rechazado'
  }
};

const MyOrders = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  useEffect(() => {
    loadOrders();
  }, []);

  const loadOrders = async () => {
    try {
      setLoading(true);
      setError('');
      const result = await orderService.getMyOrders();

      if (result.success) {
        setOrders(result.data);
      } else {
        setError(result.message);
      }
    } catch (err) {
      console.error('Error al cargar pedidos:', err);
      setError('Error al cargar los pedidos');
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = (order) => {
    setSelectedOrder(order);
    setDetailOpen(true);
  };

  const handleCancelOrder = async (orderId) => {
    if (window.confirm('¿Estás seguro de que deseas cancelar este pedido?')) {
      const result = await orderService.cancel(orderId);
      if (result.success) {
        loadOrders();
        setDetailOpen(false);
      } else {
        alert(result.message);
      }
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS'
    }).format(price || 0);
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('es-AR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <Box className="d-flex justify-center align-center min-h-screen">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/catalog-client')}
          sx={{ mb: 2 }}
        >
          Volver a nuestra cava
        </Button>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
          Mis Pedidos
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Historial y estado de tus pedidos
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {orders.length === 0 ? (
        <Card>
          <CardContent>
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <ShoppingBag sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                No tienes pedidos aún
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Visitá nuestra cava y realizá tu primer pedido
              </Typography>
              <Button
                variant="contained"
                onClick={() => navigate('/catalog-client')}
              >
                Ir a nuestra cava
              </Button>
            </Box>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={3}>
          {orders.map((order) => {
            const statusInfo = STATUS_CONFIG[order.status] || STATUS_CONFIG.pendiente;
            return (
              <Grid item xs={12} key={order.id}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Box>
                        <Typography variant="h6" gutterBottom>
                          Pedido #{order.orderNumber}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatDate(order.createdAt)}
                        </Typography>
                      </Box>
                      <Chip
                        icon={statusInfo.icon}
                        label={statusInfo.label}
                        color={statusInfo.color}
                        size="small"
                      />
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary">
                          Productos
                        </Typography>
                        <Typography variant="body1">
                          {order.items?.length || 0} item(s)
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary">
                          Total
                        </Typography>
                        <Typography variant="h6" color="primary">
                          {formatPrice(order.total)}
                        </Typography>
                      </Grid>
                    </Grid>

                    {order.notes && (
                      <Box sx={{ mt: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                          Notas:
                        </Typography>
                        <Typography variant="body2">
                          {order.notes}
                        </Typography>
                      </Box>
                    )}

                    {order.rejectionReason && (
                      <Alert severity="error" sx={{ mt: 2 }}>
                        <Typography variant="caption" sx={{ fontWeight: 600 }}>
                          Motivo de rechazo:
                        </Typography>
                        <Typography variant="body2">
                          {order.rejectionReason}
                        </Typography>
                      </Alert>
                    )}

                    <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<Visibility />}
                        onClick={() => handleViewDetails(order)}
                      >
                        Ver Detalles
                      </Button>
                      {order.status === 'pendiente' && (
                        <Button
                          variant="outlined"
                          color="error"
                          size="small"
                          onClick={() => handleCancelOrder(order.id)}
                        >
                          Cancelar Pedido
                        </Button>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Dialog de Detalles */}
      <Dialog
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              Pedido #{selectedOrder?.orderNumber}
            </Typography>
            {selectedOrder && (
              <Chip
                icon={STATUS_CONFIG[selectedOrder.status]?.icon}
                label={STATUS_CONFIG[selectedOrder.status]?.label}
                color={STATUS_CONFIG[selectedOrder.status]?.color}
                size="small"
              />
            )}
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedOrder && (
            <>
              <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                Fecha: {formatDate(selectedOrder.createdAt)}
              </Typography>

              <Typography variant="subtitle2" sx={{ mt: 3, mb: 2 }}>
                Productos:
              </Typography>
              <List>
                {selectedOrder.items?.map((item, index) => (
                  <React.Fragment key={item.id}>
                    <ListItem alignItems="flex-start">
                      <ListItemAvatar>
                        <Avatar
                          variant="rounded"
                          src={item.product?.image ? uploadService.getImageUrl(item.product.image) : '/placeholder-product.png'}
                          alt={item.productName}
                        />
                      </ListItemAvatar>
                      <ListItemText
                        primary={item.productName}
                        secondary={
                          <Box component="div">
                            <Typography variant="caption" display="block" component="span">
                              Cantidad: {item.quantity} x {formatPrice(item.unitPrice)}
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 500, mt: 0.5 }} component="div">
                              Subtotal: {formatPrice(item.subtotal)}
                            </Typography>
                          </Box>
                        }
                        secondaryTypographyProps={{ component: 'div' }}
                      />
                    </ListItem>
                    {index < selectedOrder.items.length - 1 && <Divider component="li" />}
                  </React.Fragment>
                ))}
              </List>

              <Box sx={{ mt: 2, p: 2, bgcolor: 'primary.light', borderRadius: 1 }}>
                <Typography variant="h6" sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Total:</span>
                  <span>{formatPrice(selectedOrder.total)}</span>
                </Typography>
              </Box>

              {selectedOrder.notes && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Notas:
                  </Typography>
                  <Box sx={{ p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                    <Typography variant="body2">
                      {selectedOrder.notes}
                    </Typography>
                  </Box>
                </Box>
              )}

              {selectedOrder.rejectionReason && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Motivo de rechazo:
                  </Typography>
                  <Typography variant="body2">
                    {selectedOrder.rejectionReason}
                  </Typography>
                </Alert>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          {selectedOrder?.status === 'pendiente' && (
            <Button
              color="error"
              onClick={() => handleCancelOrder(selectedOrder.id)}
            >
              Cancelar Pedido
            </Button>
          )}
          <Button onClick={() => setDetailOpen(false)}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default MyOrders;

